import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck } from 'lucide-react';
import { publicAPI, transportAPI } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from "@/contexts/AuthContext";
import { handleSubmit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import LocationEditor from "@/components/LocationEditor";
import ImageUpload from "@/components/ImageUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = ['trailer', 'van', 'lorry', 'international_lorry'];
const PRICING_UNITS = ['trip', 'km', 'hour', 'day'];
const SERVICES = [
	'climate_controlled',
	'cctv',
	'groom_onboard',
	'international',
	'customs',
	'stallion_safe',
	'insurance',
];

export default function CreateTransportAdPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
	const { user } = useAuth();
	const [images, setImages] = useState([]);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);
	const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		pricing_unit: 'trip',
		vehicle_type: 'trailer',
		capacity: 2,
		coverage_area: '',
		services: [],
		company_name: '',
		license_number: '',
		contact_phone: '',
		location_name: '',
		location_lat: null,
		location_lng: null,
		origin_name: '',
		origin_lat: null,
		origin_lng: null,
		destination_name: '',
		destination_lat: null,
		destination_lng: null,
		is_featured: false,
		featured_paid: false,
		hidden_images: []
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		publicAPI.settings()
			.then(r => setPricing(
				prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			transportAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
					pricing_unit: ad.pricing_unit || 'trip',
					vehicle_type: ad.vehicle_type || 'trailer',
					capacity: ad.capacity || 2,
					coverage_area: ad.coverage_area || '',
					services: ad.services || [],
					company_name: ad.company_name || '',
					license_number: ad.license_number || '',
					contact_phone: ad.contact_phone || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					origin_name: ad.origin_name || '',
					origin_lat: ad.origin_lat || null,
					origin_lng: ad.origin_lng || null,
					destination_name: ad.destination_name || '',
					destination_lat: ad.destination_lat || null,
					destination_lng: ad.destination_lng || null,
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
				});
				// Load existing images as thumbnails

				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);

			}).catch(() => navigate('/dashboard'));
		}
	}, [user, editId, navigate]);

	const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

	const toggleService = (s) => {
		setForm((p) => ({
			...p,
			services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s],
		}));
	};

	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) => setImages(prev => prev.map((img, idx) => idx === i ? { ...img, enabled: !img.enabled } : img));

	const submit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.description.trim() || !form.price || !form.location_name) {
			toast.error(t('common.fill_required'));
			return;
		}

		const cost = computeCosts(images, [], pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);

		// Compute hidden lists from existing media
		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);

		const payload = {
			...form,
			price: parseFloat(form.price),
			capacity: form.capacity ? parseInt(form.capacity, 10) : null,
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			hidden_images: hiddenImgs,
		};

		await handleSubmit(payload, transportAPI, isClone ? null : editId, 'transport', cost,
			newImages, [],	form.is_featured, form.featured_paid, setSaving, t, navigate);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="create-transport-page">
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

				<BackLink title={form.title} uri="/transports" textKey='nav.back_to_results' />

				<div className="flex items-center gap-3 mb-2">
					<Truck className="w-6 h-6 text-[#8B4513]" />
					<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{ isClone ? t('transport.create_title') : editId ? t('transport.edit_title') : t('transport.create_title')}
					</h1>
				</div>
				<p className="text-sm text-[#666055] mb-6">{t('transport.create_subtitle')}</p>

				<form onSubmit={submit} className="space-y-6"
							data-testid="transport-form">
					<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 space-y-4">
						<div>
							<Label>{t('horse.title_label')} *</Label>
							<Input value={form.title} onChange={(e) => update('title', e.target.value)} required
										 maxLength={120} className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
										 data-testid="transport-title" />
						</div>

						<div>
							<Label>{t('horse.description')} *</Label>
							<Textarea value={form.description} onChange={(e) => update('description', e.target.value)}
												required rows={5} maxLength={4000}
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1"
												data-testid="transport-description" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<div>
								<Label>{t('horse.price')} (€) *</Label>
								<Input type="number" min={0} step="any" value={form.price}
											 onChange={(e) => update('price', e.target.value)} required
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="transport-price" />
							</div>
							<div>
								<Label>{t('transport.pricing_unit')}</Label>
								<Select value={form.pricing_unit} onValueChange={(v) => update('pricing_unit', v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>{PRICING_UNITS.map(u =>
										<SelectItem key={u} value={u}>{t(`transport.unit.${u}`)}</SelectItem>)}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>{t('transport.capacity')}</Label>
								<Input type="number" min={1} max={20} value={form.capacity}
											 onChange={(e) => update('capacity', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label>{t('transport.vehicle_type')}</Label>
								<Select value={form.vehicle_type} onValueChange={(v) => update('vehicle_type', v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
																 data-testid="transport-vehicle-type">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>{VEHICLE_TYPES.map(v =>
										<SelectItem key={v} value={v}>{t(`transport.vehicle.${v}`)}</SelectItem>)}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>{t('transport.coverage_area')}</Label>
								<Input value={form.coverage_area}
											 onChange={(e) => update('coverage_area', e.target.value)}
											 placeholder="e.g. Portugal & Spain, EU-wide"
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>

						<div>
							<Label className="block mb-2">{t('transport.services')}</Label>
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
								{SERVICES.map((s) => (
									<label key={s} className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
										<Checkbox checked={form.services.includes(s)}
															onCheckedChange={() => toggleService(s)}
															data-testid={`transport-service-${s}`} />
										{t(`transport.service.${s}`)}
									</label>
								))}
							</div>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label>{t('transport.company_name')}</Label>
								<Input value={form.company_name} onChange={(e) => update('company_name', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
							<div>
								<Label>{t('transport.license')}</Label>
								<Input value={form.license_number} onChange={(e) => update('license_number', e.target.value)}
											 placeholder="WATO-PT-XXXX" className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>

						<div>
							<Label>{t('horse.contact_phone')}</Label>
							<Input value={form.contact_phone} onChange={(e) => update('contact_phone', e.target.value)}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
						</div>
					</div>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ update }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ update } t={ t } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						editId={ editId }
						t={ t }/>

					{/* only display if there is a cost */ }
					<WaiverCheckBox
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ saving }
						label='transport.create_ad'
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

				</form>
			</div>
		</div>
	);
}